import { useIntl } from "react-intl";
import React, { useState, useEffect, useRef, CSSProperties, useCallback } from "react";
import { Segment, Checkbox, CheckboxProps, List } from "semantic-ui-react";

//Common
import SearchBox from "react-lib/apps/common/SearchBox";
import DateTextBox from "react-lib/apps/common/DateTextBox";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import { getEffectiveZoom, getViewportRect } from "./diagram/screenGeometry";

type ListChecked = {
  [key: string]: any;
  is_selected: boolean | null;
  is_show: boolean | null;
};

type ModalItemFilterProps = {
  setProp?: any;
  buttonLoadCheck?: any;
  data?: any[] | DateType;
  textField: string;
  style?: CSSProperties;
  name: string;
  target?: string;
  classNameZoom?: string | string[];
  typeDate?: boolean;
  buttonLoadKey?: string;
  // Callback
  onClose?: () => any;
  onSelect?: (list: any[] | DateType, filterArray: string[]) => any;
};

type DateType = { startDate: string; endDate: string };

const ModalItemFilterInitial: ModalItemFilterProps = {
  data: [],
  textField: "",
  style: {},
  name: "",
  typeDate: false,
  // Callback
  onClose: () => null,
  onSelect: () => null,
};

// วาง popup ใต้ไอคอน filter ของคอลัมน์ (target) และไม่ให้ล้นจอ
const POPUP_MARGIN = 8;

export const applyTransformStyle = (target?: string, _classNameZoom?: string | string[]) => {
  const segment = document.querySelector(".scn-modal-item-filter") as HTMLDivElement | null;
  const targetElm = target ? document.querySelector(`.${target}`) : null;

  if (!segment || !targetElm) {
    return;
  }

  // ตำแหน่งตั้งต้นของ popup (position: fixed ไม่มี top/left) ก่อน translate
  segment.style.transform = "none";

  const base = getViewportRect(segment);
  const targetRect = getViewportRect(targetElm);
  // translate อยู่ใต้ CSS zoom ของ container (เช่น .treatment-today 75%)
  const zoom = getEffectiveZoom(segment.parentElement) || 1;

  const maxLeft = window.innerWidth - base.width - POPUP_MARGIN;
  const left = Math.max(POPUP_MARGIN, Math.min(targetRect.left + targetRect.width / 2 - base.width / 2, maxLeft));
  const maxTop = window.innerHeight - base.height - POPUP_MARGIN;
  const top = Math.max(POPUP_MARGIN, Math.min(targetRect.top + targetRect.height + 4, maxTop));

  segment.style.transform = `translate(${(left - base.left) / zoom}px, ${(top - base.top) / zoom}px)`;
  segment.style.opacity = `1`;
};

const ModalItemFilterCustom: React.FC<ModalItemFilterProps> = (props) => {
  const intl = useIntl();
  const [items, setItems] = useState<ListChecked[]>([]);
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);

  const [dateDetail, setDateDetail] = useState<DateType>({ startDate: "", endDate: "" });

  // Ref
  const segmentHoverRef = useRef<any>(false);

  const handleMouseClick = useCallback((e: any) => {
    if (!segmentHoverRef.current) {
      props.onClose?.();

      clearState();
    }
  }, []);

  const handleOnScroll = useCallback(() => {
    applyTransformStyle(props.target, props.classNameZoom);
  }, []);

  const removeEventListener = useCallback(() => {
    document.removeEventListener("click", handleMouseClick);
    document.removeEventListener("scroll", handleOnScroll, true);
    window.removeEventListener("resize", handleOnScroll);
  }, [handleMouseClick, handleOnScroll]);

  useEffect(() => {
    removeEventListener();

    document.addEventListener("click", handleMouseClick);
    document.addEventListener("scroll", handleOnScroll, true);
    window.addEventListener("resize", handleOnScroll);

    handleOnScroll();
    return () => {
      removeEventListener();
    };
  }, [props.target, props.classNameZoom, removeEventListener]);

  useEffect(() => {
    if (Array.isArray(props.data)) {
      setCheckedData(props.data, null, true);
    } else if (props.data) {
      setDateDetail(props.data);
    }
  }, [props.data]);

  const setCheckedData = (data?: ListChecked[], setFieldCheck: boolean | null = null, setFieldShow: boolean | null = null) => {
    let setShow = false;
    let setCheck = false;

    if (setFieldShow !== null) {
      setShow = true;
    }
    if (setFieldCheck !== null) {
      setCheck = true;
    }

    const list = (data || []).map((acc) => ({
      ...acc,
      is_selected: setCheck ? setFieldCheck : acc.is_selected,
      is_show: setShow ? setFieldShow : acc.is_show,
    }));

    if (list[0] && list.every((item) => item.is_selected)) {
      setIsSelectAll(true);
    } else {
      setIsSelectAll(false);
    }

    setItems(list);
  };

  const clearState = () => {
    setItems([]);
    setIsSelectAll(false);
    segmentHoverRef.current = false;
  };

  const handleChange = (index: number, checked: boolean) => {
    const cloneItems = [...items];
    cloneItems[index].is_selected = checked;

    if (cloneItems.every((item) => item.is_selected)) {
      setIsSelectAll(true);
    } else if (isSelectAll) {
      setIsSelectAll(false);
    }

    setItems(cloneItems);
  };

  const handleToggleSelectAll = (event: React.FormEvent<HTMLInputElement>, { checked }: CheckboxProps) => {
    setCheckedData(items, checked);
  };

  const handleGetSearchOptions = ({ searchText = "" }) => {
    const filterItems = items.map((item) => (item?.[props.textField].includes(searchText) ? { ...item, is_show: true } : { ...item, is_show: false }));

    setItems(filterItems);
    return [[], null];
  };

  const handleFilter = () => {
    const newArr = [...items];
    const itemsCheck: Record<string, any>[] = newArr.filter((item) => item.is_selected);
    const filterArray = itemsCheck.map((item) => item.name);

    for (const item of itemsCheck) {
      delete item?.is_show;
    }

    props.onSelect?.(props.typeDate ? dateDetail : itemsCheck, filterArray);
  };

  return (
    <Segment
      className="scn-modal-item-filter"
      style={{
        position: "fixed",
        background: "#f2f2f2",
        zIndex: 9,
        width: "300px",
        opacity: 0,
        ...props.style,
        // ...transformStyle,
      }}
      onMouseOver={() => (segmentHoverRef.current = true)}
      onClick={() => (segmentHoverRef.current = true)}
      onMouseLeave={() => (segmentHoverRef.current = false)}
    >
      <div style={{ display: "flex", marginBottom: "6px" }}>
        {intl.formatMessage({ id: "cukey79" })} <label style={{ fontWeight: "bold", paddingLeft: "5px" }}>{props.name}</label>
      </div>
      {props.typeDate ? (
        <div>
          <div style={{ width: "100%", display: "flex", alignItems: "center" }}>
            <div style={{ width: "20%" }}>Form</div>
            <div style={{ width: "80%" }}>
              <DateTextBox
                inputStyle={{ width: "90%" }}
                value={dateDetail.startDate}
                onChange={(value: any) => setDateDetail({ ...dateDetail, startDate: value })}
              />
            </div>
          </div>
          <div style={{ width: "100%", display: "flex", alignItems: "center", margin: "10px 0px" }}>
            <div style={{ width: "20%" }}>To</div>
            <div style={{ width: "80%" }}>
              <DateTextBox
                inputStyle={{ width: "90%" }}
                value={dateDetail.endDate}
                onChange={(value: any) => setDateDetail({ ...dateDetail, endDate: value })}
              />
            </div>
          </div>
        </div>
      ) : (
        <div>
          <SearchBox inputStyle={{ width: "100%" }} onGetSearchOptions={handleGetSearchOptions} minCharacters={0} />
          <Checkbox
            label="Select All"
            style={{ fontWeight: "bold", padding: "6px", width: "100%", border: "1px solid #e1e1e1" }}
            checked={isSelectAll}
            onChange={handleToggleSelectAll}
          />
          <div style={{ height: "130px", background: "white" }}>
            <List style={{ height: "120px", overflow: "auto", background: "white", padding: "10px 7px" }}>
              {items.map((item, index) =>
                item.is_show ? (
                  <List.Item key={index} style={{ paddingBottom: "10px" }}>
                    <Checkbox
                      key={"list-check" + index}
                      label={item.name}
                      style={{ paddingBottom: 0 }}
                      checked={item.is_selected || false}
                      onChange={(e, { checked = false }) => {
                        handleChange(index, checked);
                      }}
                    />
                  </List.Item>
                ) : null
              )}
            </List>
          </div>
        </div>
      )}

      <ButtonLoadCheck
        // function
        setProp={props.setProp}
        onClick={handleFilter}
        // data
        paramKey={props.buttonLoadKey}
        buttonLoadCheck={props.buttonLoadCheck?.[props.buttonLoadKey || ""]}
        // buttonLoadCheck="LOADING"
        // config
        color="blue"
        // size="small"
        fluid={true}
        title={intl.formatMessage({ id: "cukey78" })}
      />
    </Segment>
  );
};

ModalItemFilterCustom.defaultProps = ModalItemFilterInitial;

export default React.memo(ModalItemFilterCustom);
